import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
import { spaNavigate } from "../../../../../../../_shared/_logic/navigation"
import {
  mdiAccountCheckOutline,
  mdiAccountSwitchOutline,
  mdiCalendarCheckOutline,
  mdiCardAccountDetailsOutline,
  mdiDownload,
  mdiOpenInApp,
  mdiOpenInNew,
} from "@mdi/js"
import InboxCaseWorkflowOverlay from "../_modules/inbox-case-workflow-overlay"
import { resolveInboxCaseOverlayTitle } from "../_logic/presentation"
import {
  hasInboxCaseWorkflowTodo,
  inboxCaseActionCaseIds,
  inboxCaseWorkflowActionCaseIds,
  isActionableInboxCaseTodo,
} from "../_logic/inboxCaseTodo"
import { invalidateNotifications } from "@kontextwerk/control-web-frame/components/notifications/logic/invalidation"
import type { Notification, OrganizationFrame } from "@kontextwerk/control-web-client"
import type {
  NotificationActionHelpers,
  NotificationRowActionRegistration,
  NotificationRow,
} from "@kontextwerk/control-web-frame/components/notifications/NotificationsTable.types"
import type { TableRowAction } from "@kontextwerk/web-sdk/core/layout/table"
interface Props {
  todo: Notification
  row: NotificationRow
  helpers: NotificationActionHelpers
  organization?: OrganizationFrame | null
  trialRestrictionsActive?: boolean
  actorIsAdmin?: boolean
  onResumeTodo?: (payload: unknown, todo: Notification) => void
  onOpenLetter?: (payload: unknown, todo: Notification) => void
}
type RescheduleTodoPayload = {
  kind?: string
  resume_step?: string | null
  work_order_id?: string | null
  canceled_event?: unknown
  new_event?: unknown
}
type LetterTodoPayload = {
  path: string
  downloadPath: string | null
  organizationId: string | null
  workOrderId: string | null
  workOrderNumber: string | null
}
type InboxCaseActionPresentation = {
  batchLabel: string
  batchGroup: string
  batchOrder: number
  iconPath: string
}
export default function buildTodoActions({
  todo,
  row,
  helpers,
  organization = null,
  trialRestrictionsActive = false,
  actorIsAdmin = false,
  onResumeTodo,
  onOpenLetter,
}: Props) {
  if (todo.manual) {
    return { actions: [], workflowPrimaryActionId: null, batchActions: [] }
  }

  const normalizeReschedulePayload = (payload: unknown): RescheduleTodoPayload | null => {
    if (!payload || typeof payload !== "object") return null

    const candidate = payload as RescheduleTodoPayload

    if (candidate.kind && candidate.kind !== "reschedule_flow") return null

    if (
      candidate.resume_step ||
      candidate.work_order_id ||
      candidate.canceled_event ||
      candidate.new_event
    )
      return candidate

    return null
  }
  const normalizeLetterPayload = (todo: Notification): LetterTodoPayload | null => {
    if (!todo?.payload || typeof todo.payload !== "object") return null

    const raw = todo.payload as Record<string, unknown>
    const kind = typeof raw.kind === "string" ? raw.kind.toLowerCase() : null
    const isLetterTodo =
      kind === "outreach_letter" || (todo.event_type ?? "").includes("outreach.letter")

    if (!isLetterTodo) return null

    const workOrderId = typeof raw.work_order_id === "string" ? (raw.work_order_id as string) : null
    const workOrderNumber =
      typeof raw.work_order_number === "string" ? (raw.work_order_number as string) : null
    const downloadPath =
      typeof raw.download_path === "string" && raw.download_path
        ? (raw.download_path as string)
        : null
    const organizationId =
      typeof raw.organization_id === "string"
        ? (raw.organization_id as string)
        : todo.organization_id || organization?.id || null
    const path =
      typeof raw.path === "string" && raw.path
        ? (raw.path as string)
        : organizationId && workOrderId
          ? `/organizations/${organizationId}/work-orders/${workOrderId}/letter`
          : null

    if (!path) return null

    return {
      path,
      downloadPath,
      organizationId,
      workOrderId,
      workOrderNumber,
    }
  }
  const notificationPayload = (candidate: Notification): Record<string, unknown> =>
    candidate?.payload && typeof candidate.payload === "object"
      ? (candidate.payload as Record<string, unknown>)
      : {}
  const todoHasOpenActionCases = (candidate: Notification): boolean =>
    inboxCaseActionCaseIds(candidate).length > 0
  const resumePayload = normalizeReschedulePayload(todo?.payload)
  const letterPayload = normalizeLetterPayload(todo)
  const inboxCasePayload = (() => notificationPayload(todo))()
  const inboxCaseIds = inboxCaseActionCaseIds(todo)
  const workflowInboxCaseIds = inboxCaseWorkflowActionCaseIds(todo)
  const inboxCaseFlowKey =
    typeof inboxCasePayload.flow_key === "string" ? inboxCasePayload.flow_key.trim() : ""
  const hasInboxCaseWorkflow = Boolean(
    workflowInboxCaseIds.length && inboxCaseFlowKey && hasInboxCaseWorkflowTodo(todo),
  )
  const canBatchInboxCaseWorkflow = Boolean(
    inboxCaseIds.length && hasInboxCaseWorkflow && isActionableInboxCaseTodo(todo),
  )
  const inboxCaseActionPresentation = (() => {
    switch (inboxCaseFlowKey) {
      case "customer_contact_verification":
        return {
          batchLabel: "Ansprechpartner zuordnen",
          batchGroup: "Ansprechpartner",
          batchOrder: 10,
          iconPath: mdiAccountCheckOutline,
        }
      case "customer_contact_point_review":
        return {
          batchLabel: "Kontaktdaten prüfen",
          batchGroup: "Ansprechpartner",
          batchOrder: 20,
          iconPath: mdiCardAccountDetailsOutline,
        }
      case "wrong_recipient_replacement":
        return {
          batchLabel: "Ansprechpartner austauschen",
          batchGroup: "Ansprechpartner",
          batchOrder: 30,
          iconPath: mdiAccountSwitchOutline,
        }
      case "unclear_appointment_feedback":
        return {
          batchLabel: "Terminrücklauf klären",
          batchGroup: "Termine",
          batchOrder: 20,
          iconPath: mdiCalendarCheckOutline,
        }
      case "letter_delivery_batch":
        return {
          batchLabel: "Terminbriefe zustellen",
          batchGroup: "Termine",
          batchOrder: 10,
          iconPath: mdiDownload,
        }
      default:
        return {
          batchLabel: "Inbox-Fälle bearbeiten",
          batchGroup: "Allgemein",
          batchOrder: 100,
          iconPath: mdiOpenInApp,
        }
    }
  })()
  const openInboxCaseBatchOverlay = (selectedTodos: Notification[]) => {
    if (!organization) {
      newNotification({ class: "error", html: "Keine Organisation geladen." })

      return
    }

    const workflowTodos = selectedTodos.filter(
      (selectedTodo) => inboxCaseWorkflowActionCaseIds(selectedTodo).length > 0,
    )

    if (!workflowTodos.length) {
      newNotification({ class: "error", html: "Keine passenden Inbox-Fälle ausgewählt." })

      return
    }

    const overlayTitle =
      workflowTodos.length === 1
        ? resolveInboxCaseOverlayTitle(workflowTodos[0])
        : inboxCaseActionPresentation.batchLabel
    openOverlay({
      title: overlayTitle,
      content: InboxCaseWorkflowOverlay,
      bodyPadding: "none",
      closeOnParentOpen: true,
      defaultFullscreen: true,
      properties: {
        todos: workflowTodos,
        organization,
        trialRestrictionsActive,
        actorIsAdmin,
        helpers,
        onFinished: async () => {
          await invalidateNotifications(organization.id)
        },
        readOnly: selectedTodos.every(
          (selectedTodo) =>
            !isActionableInboxCaseTodo(selectedTodo) || !todoHasOpenActionCases(selectedTodo),
        ),
      },
    })
  }
  const handleOpenInboxCase = () => {
    openInboxCaseBatchOverlay([todo])
  }
  const handleOpenLetter = () => {
    const payload = letterPayload

    if (!payload) return

    if (onOpenLetter) {
      onOpenLetter(payload, todo)

      return
    }

    if (payload.path) {
      spaNavigate(payload.path, { noSearchParam: false })
    }
  }
  let downloadingLetter = false
  const handleDownloadLetter = async () => {
    const payload = letterPayload

    if (!payload?.downloadPath || downloadingLetter) return

    downloadingLetter = true
    try {
      const response = await fetch(payload.downloadPath, { credentials: "include" })

      if (!response.ok) {
        throw new Error(`Letter download failed (${response.status})`)
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      const number = payload.workOrderNumber?.trim()
      anchor.href = objectUrl
      anchor.download = number ? `Terminbrief_${number}.pdf` : "Terminbrief.pdf"
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (err) {
      console.error("letter_todo_download_failed", err)
      newNotification({ class: "error", html: "Terminbrief konnte nicht heruntergeladen werden." })
    } finally {
      downloadingLetter = false
    }
  }
  const handleResume = () => {
    const payload = resumePayload

    if (!payload) return

    if (onResumeTodo) {
      onResumeTodo(payload, todo)
    }
  }
  const rowActions = (() => {
    const actions: NotificationRowActionRegistration[] = []

    if (canBatchInboxCaseWorkflow) {
      actions.push({
        id: `kehrblatt.inbox-case.${inboxCaseFlowKey}`,
        batchLabel: inboxCaseActionPresentation.batchLabel,
        batchCompatible: true,
        batchGroup: inboxCaseActionPresentation.batchGroup,
        batchOrder: inboxCaseActionPresentation.batchOrder,
        iconPath: inboxCaseActionPresentation.iconPath,
        refreshPolicy: "none",
        notification: todo,
        tooltipContent: resolveInboxCaseOverlayTitle(todo),
        run: handleOpenInboxCase,
        runBatch: async (entries) => {
          const selectedTodos = entries
            .map((entry) => entry.notification)
            .filter((entry): entry is Notification => Boolean(entry))
          openInboxCaseBatchOverlay(selectedTodos.length ? selectedTodos : [todo])
        },
      })
    }

    if (letterPayload?.downloadPath) {
      actions.push({
        id: "kehrblatt.download-letter",
        batchLabel: "Terminbrief herunterladen",
        batchCompatible: false,
        batchGroup: "Dokumente",
        batchOrder: 10,
        iconPath: mdiDownload,
        refreshPolicy: "none",
        notification: todo,
        run: async () => {
          await handleDownloadLetter()
        },
      })
    }

    return actions
  })()
  const actions: TableRowAction[] = []

  if (hasInboxCaseWorkflow) {
    actions.push({
      id: "action-0",
      muted: helpers.isDone,
      label:
        helpers.isDone && !isActionableInboxCaseTodo(todo)
          ? `${resolveInboxCaseOverlayTitle(todo)} ansehen`
          : resolveInboxCaseOverlayTitle(todo),
      onClick: () => handleOpenInboxCase(),
      icon: inboxCaseActionPresentation.iconPath,
      tooltip:
        helpers.isDone && !isActionableInboxCaseTodo(todo)
          ? `${resolveInboxCaseOverlayTitle(todo)} ansehen`
          : resolveInboxCaseOverlayTitle(todo),
    })
  }

  if (letterPayload) {
    actions.push({
      id: "action-1",
      display: "inline",
      label: "Terminbrief mit dem Link zu diesem Auftrag öffnen.",
      onClick: () => handleOpenLetter(),
      icon: mdiOpenInNew,
      tooltip: "Terminbrief mit dem Link zu diesem Auftrag öffnen.",
    })

    actions.push({
      id: "action-2",
      display: "inline",
      label: "Terminbrief als PDF herunterladen.",
      onClick: () => handleDownloadLetter(),
      icon: mdiDownload,
      disabled: !letterPayload.downloadPath || downloadingLetter,
      tooltip: "Terminbrief als PDF herunterladen.",
    })
  }

  if (resumePayload && onResumeTodo) {
    actions.push({
      id: "action-3",
      muted: helpers.isDone,
      label: "Verbundene Aktion durchführen",
      onClick: () => handleResume(),
      icon: mdiOpenInApp,
      tooltip: "Verbundene Aktion durchführen",
    })
  }

  return {
    actions,
    workflowPrimaryActionId: hasInboxCaseWorkflow ? "action-0" : null,
    batchActions: rowActions,
  }
}
